/**
 * REGRIND_RECYCLE_PRD §FR-A — Regrind RM creation fields.
 *
 * Per user direction (Feb 2026):
 *   1. Polymer ID — single-pick dropdown of existing POLY rm_ids.
 *   2. Polymer Grade — auto-filled, read-only; derived from the chosen POLY.
 *   3. Colour — open text.
 *
 * Backend `POST /raw-materials` re-validates polymer_id against the POLY
 * collection and re-stamps polymer_grade from the linked POLY, so a stale
 * frontend can never persist a mismatch.
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegrindRMFields({ categoryData, setCategoryData }) {
  const [polyOptions, setPolyOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchPoly = async () => {
      try {
        const r = await axios.get(`${API}/raw-materials`, {
          params: { category: "POLY", limit: 5000 },
        });
        if (cancelled) return;
        const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        // Defensive client-side filter — the backend currently ignores
        // the `category` query param (testing-agent finding, Feb 2026),
        // so without this filter the dropdown would show every RM.
        const filtered = rows.filter(x => (x.category || "").toUpperCase() === "POLY");
        // Sort by rm_id so the dropdown is predictable.
        filtered.sort((a, b) => (a.rm_id || "").localeCompare(b.rm_id || ""));
        setPolyOptions(filtered);
      } catch (e) {
        // Soft-fail — the dropdown just stays empty + user sees a warning.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPoly();
    return () => { cancelled = true; };
  }, []);

  const selectedPoly = useMemo(
    () => polyOptions.find(p => p.rm_id === categoryData.polymer_id) || null,
    [polyOptions, categoryData.polymer_id],
  );

  // Auto-derive polymer_grade whenever polymer_id changes.
  useEffect(() => {
    if (selectedPoly) {
      const g = selectedPoly?.category_data?.grade || "";
      if ((categoryData.polymer_grade || "") !== g) {
        setCategoryData(prev => ({ ...prev, polymer_grade: g }));
      }
    } else if (categoryData.polymer_grade) {
      setCategoryData(prev => ({ ...prev, polymer_grade: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPoly?.rm_id]);

  return (
    <div className="space-y-3">
      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
        <p className="text-sm font-medium text-emerald-900 mb-1">Reground Polymer</p>
        <p className="text-[11px] text-emerald-800">
          Pick the parent polymer this regrind came from. Polymer Grade is
          auto-filled from your selection — the two can never mismatch.
        </p>
      </div>

      <div>
        <Label>Polymer ID <span className="text-red-600">*</span></Label>
        <Select
          value={categoryData.polymer_id || ""}
          onValueChange={(v) => setCategoryData(prev => ({ ...prev, polymer_id: v }))}
        >
          <SelectTrigger data-testid="regrind-polymer-id-select">
            <SelectValue placeholder={loading ? "Loading polymers…" : "Select POLY raw material"} />
          </SelectTrigger>
          <SelectContent>
            {polyOptions.length === 0 && !loading ? (
              <SelectItem value="__none__" disabled>No POLY raw materials found</SelectItem>
            ) : polyOptions.map(p => {
              const g = p?.category_data?.grade || "";
              const t = p?.category_data?.type || "";
              const label = [p.rm_id, t && g ? `${t} ${g}` : (t || g)]
                .filter(Boolean).join(" — ");
              return (
                <SelectItem key={p.rm_id} value={p.rm_id} data-testid={`regrind-poly-option-${p.rm_id}`}>
                  {label}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Polymer Grade</Label>
        <Input
          value={categoryData.polymer_grade || ""}
          readOnly
          disabled
          placeholder="Auto-filled from selected Polymer ID"
          data-testid="regrind-polymer-grade-readonly"
          className="bg-gray-50 font-mono text-sm"
        />
        <p className="text-[10px] text-muted-foreground mt-0.5">
          Read-only — sourced from POLY master so regrind never drifts from its parent.
        </p>
      </div>

      <div>
        <Label>Colour <span className="text-red-600">*</span></Label>
        <Input
          value={categoryData.colour || ""}
          onChange={(e) => setCategoryData(prev => ({ ...prev, colour: e.target.value }))}
          placeholder="e.g. Black, Natural, Red"
          data-testid="regrind-colour-input"
        />
      </div>
    </div>
  );
}
